import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Plus, Trash2, Upload, ChevronDown, ChevronUp,
  DollarSign, Link, Package, Grid, Layers
} from 'lucide-react';
import uploadService from '../../../services/uploadService';
import ImageCropperModal from './ImageCropperModal';

const MODES = [
  { id: 'single', name: 'Single Product', icon: Package, desc: 'One product featured prominently' },
  { id: 'grid', name: 'Product Grid', icon: Grid, desc: '2-6 products in grid layout' },
  { id: 'carousel', name: 'Carousel', icon: Layers, desc: 'Scrollable product carousel' },
];

const CARD_STYLES = [
  { id: 'minimal', name: 'Minimal' },
  { id: 'standard', name: 'Standard' },
  { id: 'premium', name: 'Premium' },
];

const BUTTON_STYLES = [
  { id: 'solid', name: 'Solid' },
  { id: 'outline', name: 'Outline' },
  { id: 'gradient', name: 'Gradient' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

const makeDefaultProduct = () => ({
  id: '',
  name: '',
  description: '',
  shortDescription: '',
  price: 0,
  currency: 'USD',
  compareAtPrice: null,
  images: [],
  ctaText: 'Buy Now',
  ctaUrl: '',
  inventory: null,
  features: [],
});

// Custom Select Component - uses portal to avoid overflow clipping
const Select = ({ value, onChange, options, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const updatePosition = () => {
      const rect = ref.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.id === value || opt.value === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
      >
        <span>{selectedOption?.name || selectedOption?.label || 'Select...'}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.id || option.value}
              onClick={() => { onChange(option.id || option.value); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                (option.id || option.value) === value ? 'text-purple-400 bg-white/5' : 'text-gray-300'
              }`}
            >
              {option.name || option.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default function ProductShowcaseModal({
  isOpen,
  onClose,
  onSave,
  showcase = null,
  folders = [],
  currentFolder = null,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements,
}) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    folder_id: currentFolder?.id || '',
    mode: 'single',
    products: [{ ...makeDefaultProduct(), id: `prod_${Date.now()}` }],
    layout: {
      gridColumns: 2,
      carouselAutoAdvance: false,
      carouselInterval: 5000,
      showPricing: true,
      showInventory: false,
      showComparePrice: true,
      cardStyle: 'standard',
    },
    branding: {
      primaryColor: '#8B5CF6',
      accentColor: '#10B981',
      buttonStyle: 'solid',
      backgroundColor: '#0a0a1a',
    },
  });

  const [expandedProduct, setExpandedProduct] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Crop queue: when the user picks N files, we crop them one at a time.
  // cropQueue holds the remaining files; cropTargetProduct remembers which
  // product the cropped image should be attached to (since the queue may span
  // multiple async user interactions).
  const [cropQueue, setCropQueue] = useState([]);
  const [cropTargetProduct, setCropTargetProduct] = useState(null);

  useEffect(() => {
    if (showcase) {
      setFormData({
        name: showcase.name || '',
        folder_id: showcase.folder_id || currentFolder?.id || '',
        mode: showcase.mode || 'single',
        products: showcase.products || [{ ...makeDefaultProduct(), id: `prod_${Date.now()}` }],
        layout: { ...formData.layout, ...showcase.layout },
        branding: { ...formData.branding, ...showcase.branding },
      });
    }
  }, [showcase]);

  // File picker → crop queue. We do NOT upload immediately anymore; each file
  // is opened in the cropper modal first, and only the cropped Blob is sent.
  // Multi-file selections are processed one at a time in queue order.
  const handleImageUpload = (productIndex, files) => {
    if (!files?.length) return;
    const accepted = Array.from(files)
      .slice(0, 5)
      .filter((f) => f.size <= 5 * 1024 * 1024);
    if (accepted.length === 0) return;
    setCropTargetProduct(productIndex);
    setCropQueue(accepted);
  };

  // Upload a single cropped Blob to the products bucket and append it to the
  // target product's images. Called once per crop confirmation.
  //
  // IMPORTANT: this uses the functional form of setFormData so we always read
  // the *latest* products array — never a stale closure. The previous version
  // captured `formData` at function-creation time and clobbered prior images
  // when the user added a second one (bug: "1 image works, 2nd doesn't add").
  const uploadCroppedBlob = async (blob, originalFile) => {
    const productIndex = cropTargetProduct;
    if (productIndex == null) return;
    setUploading(true);
    try {
      // Wrap the Blob back into a File so the upload service sees a filename.
      // Use the original name but force a .jpg extension since the cropper
      // always emits JPEG (see ImageCropperModal.getCroppedBlob).
      const baseName = (originalFile?.name || 'image').replace(/\.[^.]+$/, '');
      const file =
        blob instanceof File
          ? blob
          : new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });

      // NOTE: uploadService.uploadFile(file, onProgress) — the second arg is
      // a progress callback, NOT a folder hint. The previous code passed the
      // string 'products' here, which was truthy inside the onUploadProgress
      // handler and threw "onProgress is not a function" mid-request. The
      // backend /upload/single endpoint hard-codes the folder anyway.
      const result = await uploadService.uploadFile(file);
      const newImage = {
        id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        url: result?.url,
        alt: originalFile?.name || 'image',
      };

      setFormData((prev) => {
        const updatedProducts = prev.products.map((p, i) => {
          if (i !== productIndex) return p;
          const nextImages = [...p.images, { ...newImage, isPrimary: p.images.length === 0 }].slice(0, 5);
          return { ...p, images: nextImages };
        });
        return { ...prev, products: updatedProducts };
      });
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  // Crop confirmed: upload this one, then advance the queue.
  const handleCropConfirm = async (blob, originalFile) => {
    await uploadCroppedBlob(blob, originalFile);
    setCropQueue((prev) => prev.slice(1));
  };

  // Crop cancelled: skip this file and advance the queue.
  const handleCropCancel = () => {
    setCropQueue((prev) => prev.slice(1));
  };

  // When the queue drains, clear the target so a stale productIndex can't
  // attach to the wrong product on the next picker session.
  useEffect(() => {
    if (cropQueue.length === 0 && cropTargetProduct != null) {
      setCropTargetProduct(null);
    }
  }, [cropQueue.length, cropTargetProduct]);

  const updateProduct = (index, field, value) => {
    const updated = [...formData.products];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, products: updated });
  };

  const addProduct = () => {
    if (formData.products.length >= 6) return;
    setFormData({
      ...formData,
      products: [...formData.products, { ...makeDefaultProduct(), id: `prod_${Date.now()}` }],
    });
    setExpandedProduct(formData.products.length);
  };

  const removeProduct = (index) => {
    if (formData.products.length <= 1) return;
    const updated = formData.products.filter((_, i) => i !== index);
    setFormData({ ...formData, products: updated });
    setExpandedProduct(Math.max(0, expandedProduct - 1));
  };

  const removeImage = (productIndex, imageIndex) => {
    const updated = [...formData.products];
    updated[productIndex].images = updated[productIndex].images.filter((_, i) => i !== imageIndex);
    setFormData({ ...formData, products: updated });
  };

  const addFeature = (productIndex) => {
    const updated = [...formData.products];
    if (!updated[productIndex].features) updated[productIndex].features = [];
    if (updated[productIndex].features.length >= 5) return;
    updated[productIndex].features.push('');
    setFormData({ ...formData, products: updated });
  };

  const updateFeature = (productIndex, featureIndex, value) => {
    const updated = [...formData.products];
    updated[productIndex].features[featureIndex] = value;
    setFormData({ ...formData, products: updated });
  };

  const removeFeature = (productIndex, featureIndex) => {
    const updated = [...formData.products];
    updated[productIndex].features = updated[productIndex].features.filter((_, i) => i !== featureIndex);
    setFormData({ ...formData, products: updated });
  };

  const handleSave = () => {
    if (!formData.name || formData.products.length === 0) return;
    if (!formData.products[0].name) return;

    onSave({
      ...formData,
      products: formData.products.filter(p => p.name),
    });
    onClose();
  };

  const canProceed = () => {
    if (step === 1) return formData.mode;
    if (step === 2) return formData.products.some(p => p.name && p.ctaUrl);
    if (step === 3) return true;
    if (step === 4) return formData.name;
    return false;
  };

  if (!isOpen) return null;

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      style={{ zIndex: 9999 }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-3xl bg-[#001233] rounded-2xl border border-white/10 overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {showcase ? 'Edit Product Showcase' : 'Create Product Showcase'}
            </h2>
            <p className="text-xs text-gray-400">Step {step} of 4</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-white/5">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-green-500 transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Read-only banner */}
          {readOnly && (
            <div className="flex items-start gap-3 mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              <p className="text-sm text-purple-200">
                This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
              </p>
            </div>
          )}

          {/* Step 1: Choose Mode */}
          {step === 1 && (
            <div className="space-y-4">
              <label className="text-sm text-gray-300">Choose Display Mode</label>
              <div className="grid grid-cols-3 gap-3">
                {MODES.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setFormData({ ...formData, mode: mode.id })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.mode === mode.id
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/10 hover:border-white/20 bg-white/5'
                    }`}
                  >
                    <mode.icon className={`w-8 h-8 mb-2 ${
                      formData.mode === mode.id ? 'text-purple-400' : 'text-gray-400'
                    }`} />
                    <p className="font-medium text-white text-sm">{mode.name}</p>
                    <p className="text-[10px] text-gray-500 mt-1">{mode.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Add Products */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-300">Products ({formData.products.length}/6)</label>
                {!readOnly && formData.products.length < 6 && (
                  <button
                    onClick={addProduct}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white/5 border border-white/20 text-white rounded-lg hover:bg-white/10"
                  >
                    <Plus className="w-3 h-3" /> Add Product
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {formData.products.map((product, idx) => (
                  <div key={product.id} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                    {/* Product Header */}
                    <button
                      onClick={() => setExpandedProduct(expandedProduct === idx ? -1 : idx)}
                      className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {product.images[0] ? (
                          <img src={product.images[0].url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-500" />
                          </div>
                        )}
                        <div className="text-left">
                          <p className="text-sm font-medium text-white">
                            {product.name || `Product ${idx + 1}`}
                          </p>
                          {product.price > 0 && (
                            <p className="text-xs text-green-400">${product.price} {product.currency}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {formData.products.length > 1 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); removeProduct(idx); }}
                            className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        )}
                        {expandedProduct === idx ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Product Details */}
                    <AnimatePresence>
                      {expandedProduct === idx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-white/10"
                        >
                          <div className="p-4 space-y-4">
                            {/* Images */}
                            <div>
                              <label className="text-xs text-gray-400 mb-2 block">Images (max 5)</label>
                              <div className="flex flex-wrap gap-2">
                                {product.images.map((img, imgIdx) => (
                                  <div key={img.id} className="relative group">
                                    <img src={img.url} alt={img.alt} className="w-16 h-16 rounded-lg object-cover" />
                                    <button
                                      onClick={() => removeImage(idx, imgIdx)}
                                      className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <X className="w-3 h-3 text-white" />
                                    </button>
                                  </div>
                                ))}
                                {!readOnly && product.images.length < 5 && (
                                  <label className="w-16 h-16 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-purple-500/50 transition-colors">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      className="hidden"
                                      // Reset value on click so picking the
                                      // SAME file twice still fires onChange.
                                      // Without this, the input ignores a
                                      // re-selection of the same filename and
                                      // the second upload silently fails.
                                      onClick={(e) => { e.target.value = null; }}
                                      onChange={(e) => handleImageUpload(idx, e.target.files)}
                                      disabled={uploading}
                                    />
                                    {uploading ? (
                                      <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <Upload className="w-5 h-5 text-gray-500" />
                                    )}
                                  </label>
                                )}
                              </div>
                            </div>

                            {/* Basic Info */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-400">Product Name *</label>
                                <input
                                  value={product.name}
                                  onChange={(e) => updateProduct(idx, 'name', e.target.value)}
                                  placeholder="Product name"
                                  className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-400">Short Description</label>
                                <input
                                  value={product.shortDescription}
                                  onChange={(e) => updateProduct(idx, 'shortDescription', e.target.value.slice(0, 100))}
                                  placeholder="Brief description (100 chars max)"
                                  className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="text-xs text-gray-400">Full Description</label>
                              <textarea
                                value={product.description}
                                onChange={(e) => updateProduct(idx, 'description', e.target.value.slice(0, 500))}
                                placeholder="Detailed description (500 chars max)"
                                className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm h-20 resize-none focus:outline-none"
                              />
                            </div>

                            {/* Pricing */}
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="text-xs text-gray-400">Price *</label>
                                <div className="relative mt-1">
                                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <input
                                    type="number"
                                    value={product.price || ''}
                                    onChange={(e) => updateProduct(idx, 'price', parseFloat(e.target.value) || 0)}
                                    placeholder="0.00"
                                    className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-gray-400">Currency</label>
                                <select
                                  value={product.currency}
                                  onChange={(e) => updateProduct(idx, 'currency', e.target.value)}
                                  className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2"
                                >
                                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-gray-400">Compare Price</label>
                                <input
                                  type="number"
                                  value={product.compareAtPrice || ''}
                                  onChange={(e) => updateProduct(idx, 'compareAtPrice', parseFloat(e.target.value) || null)}
                                  placeholder="Original"
                                  className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                />
                              </div>
                            </div>

                            {/* CTA */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-gray-400">Button Text</label>
                                <input
                                  value={product.ctaText}
                                  onChange={(e) => updateProduct(idx, 'ctaText', e.target.value)}
                                  placeholder="Buy Now"
                                  className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-400">CTA URL *</label>
                                <div className="relative mt-1">
                                  <Link className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                  <input
                                    value={product.ctaUrl}
                                    onChange={(e) => updateProduct(idx, 'ctaUrl', e.target.value)}
                                    placeholder="https://..."
                                    className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Features */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-xs text-gray-400">Features (max 5)</label>
                                {(!product.features || product.features.length < 5) && (
                                  <button
                                    onClick={() => addFeature(idx)}
                                    className="text-xs text-purple-400 hover:text-purple-300"
                                  >
                                    + Add Feature
                                  </button>
                                )}
                              </div>
                              <div className="space-y-2">
                                {product.features?.map((feature, fIdx) => (
                                  <div key={fIdx} className="flex gap-2">
                                    <input
                                      value={feature}
                                      onChange={(e) => updateFeature(idx, fIdx, e.target.value)}
                                      placeholder="Feature benefit..."
                                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                                    />
                                    <button
                                      onClick={() => removeFeature(idx, fIdx)}
                                      className="p-2 hover:bg-red-500/20 rounded-lg"
                                    >
                                      <Trash2 className="w-4 h-4 text-red-400" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Inventory */}
                            <div>
                              <label className="text-xs text-gray-400">Inventory (optional)</label>
                              <input
                                type="number"
                                min="0"
                                value={product.inventory ?? ''}
                                onChange={(e) => updateProduct(idx, 'inventory', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                placeholder="Stock count"
                                className="mt-1 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Layout & Branding */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Layout Options */}
              <div className="space-y-4">
                <label className="text-sm text-gray-300">Layout Options</label>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-400">Card Style</label>
                    <Select
                      value={formData.layout.cardStyle}
                      onChange={(v) => setFormData({
                        ...formData,
                        layout: { ...formData.layout, cardStyle: v }
                      })}
                      options={CARD_STYLES}
                    />
                  </div>

                  {formData.mode === 'grid' && (
                    <div>
                      <label className="text-xs text-gray-400">Grid Columns</label>
                      <select
                        value={formData.layout.gridColumns}
                        onChange={(e) => setFormData({
                          ...formData,
                          layout: { ...formData.layout, gridColumns: parseInt(e.target.value) }
                        })}
                        className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2"
                      >
                        <option value={2}>2 Columns</option>
                        <option value={3}>3 Columns</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.layout.showPricing}
                      onChange={(e) => setFormData({
                        ...formData,
                        layout: { ...formData.layout, showPricing: e.target.checked }
                      })}
                      className="rounded border-white/20 bg-white/5"
                    />
                    <span className="text-sm text-gray-300">Show Pricing</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.layout.showComparePrice}
                      onChange={(e) => setFormData({
                        ...formData,
                        layout: { ...formData.layout, showComparePrice: e.target.checked }
                      })}
                      className="rounded border-white/20 bg-white/5"
                    />
                    <span className="text-sm text-gray-300">Show Compare Price</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.layout.showInventory}
                      onChange={(e) => setFormData({
                        ...formData,
                        layout: { ...formData.layout, showInventory: e.target.checked }
                      })}
                      className="rounded border-white/20 bg-white/5"
                    />
                    <span className="text-sm text-gray-300">Show Inventory</span>
                  </label>
                </div>
              </div>

              {/* Branding */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <label className="text-sm text-gray-300">Branding</label>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-400">Primary Color</label>
                    <div className="mt-1 flex gap-2">
                      <input
                        type="color"
                        value={formData.branding.primaryColor}
                        onChange={(e) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, primaryColor: e.target.value }
                        })}
                        className="w-9 h-9 rounded cursor-pointer border-0"
                      />
                      <input
                        value={formData.branding.primaryColor}
                        onChange={(e) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, primaryColor: e.target.value }
                        })}
                        className="flex-1 px-2 bg-white/5 border border-white/10 rounded text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Accent Color</label>
                    <div className="mt-1 flex gap-2">
                      <input
                        type="color"
                        value={formData.branding.accentColor}
                        onChange={(e) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, accentColor: e.target.value }
                        })}
                        className="w-9 h-9 rounded cursor-pointer border-0"
                      />
                      <input
                        value={formData.branding.accentColor}
                        onChange={(e) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, accentColor: e.target.value }
                        })}
                        className="flex-1 px-2 bg-white/5 border border-white/10 rounded text-white text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Button Style</label>
                    <Select
                      value={formData.branding.buttonStyle}
                      onChange={(v) => setFormData({
                        ...formData,
                        branding: { ...formData.branding, buttonStyle: v }
                      })}
                      options={BUTTON_STYLES}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Name & Save */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-300">Showcase Name *</label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={readOnly}
                  placeholder="e.g., Holiday Sale Products"
                  className={`mt-2 w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
              </div>

              {!skipFolderSelect && folders.length > 0 && (
                <div>
                  <label className="text-sm text-gray-300">Save to Folder</label>
                  <select
                    value={formData.folder_id}
                    onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
                    className="mt-2 w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3"
                  >
                    <option value="">Select folder...</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              )}

              {/* Preview */}
              <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
                <p className="text-xs text-gray-400 mb-3">Preview Summary</p>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-500/20 to-green-500/20 flex items-center justify-center">
                    <Package className="w-8 h-8 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{formData.name || 'Untitled Showcase'}</p>
                    <p className="text-sm text-gray-400">
                      {formData.mode === 'single' ? 'Single Product' :
                       formData.mode === 'grid' ? `Grid (${formData.products.length} products)` :
                       `Carousel (${formData.products.length} products)`}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.layout.cardStyle} style • {formData.branding.buttonStyle} buttons
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 border border-white/20 text-gray-300 rounded-lg hover:bg-white/10 transition-colors"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-green-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continue
            </button>
          ) : readOnly ? (onCopyToMyElements && (
            <button
              type="button"
              onClick={onCopyToMyElements}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              Copy to My Elements
            </button>
          )) : (
            <button
              onClick={handleSave}
              disabled={!canProceed()}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-green-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save Showcase
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <>
      {ReactDOM.createPortal(modalContent, document.body)}
      {/* Image cropper — opens whenever a file is queued. The cropper portals
          itself to document.body, so it always stacks above the showcase modal
          regardless of where this JSX sits in the tree. */}
      {cropQueue.length > 0 && (
        // key forces a fresh ImageCropperModal instance for each file in the
        // queue. Without this, react-easy-crop's internal image cache and
        // crop coordinates from the previous file leak into the next one.
        <ImageCropperModal
          key={`${cropQueue[0].name}-${cropQueue[0].size}-${cropQueue[0].lastModified}`}
          file={cropQueue[0]}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
}
